import type { Building } from "building-types/building/buildingTypes";
import type { EdgeDimensionBehavior } from "building-types/edgeBehavior/edgeDimensionBehavior";
import type {
  HalfEdgeAssociationTable,
  RWHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import { getWallPath } from "building-types/walls/wallUtils";
import type { Table, RWTable } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { halfEdgeDataNew } from "doc/provenance/halfEdgeDataTools";

export const COMPOSITE_ROOM_SET_WALL_LEVEL_ID_PREFIX = "__composite_room_set_wall__";

export interface CompositeRoomSetWallIndexContext {
  wallIndex: InteriorDimensionWallIndex;
  wallLevelId: string;
  wallTable: Table<Pick<WallSystem, "walls" | "halfEdgeToWall">>;
  dimensionBehavior: HalfEdgeAssociationTable<EdgeDimensionBehavior>;
  resolveHalfEdgeData: (halfEdges: number[]) => EdgeData[];
  resolveHalfEdgeLine: (halfEdge: number) => [number, number, number, number] | undefined;
}

export type InteriorDimensionWallIndex = Pick<
  WallIndex,
  "solidWallStripsInRoomSet" | "mergeCollinearWallStripsReturnHalfEdges"
> &
  Partial<Pick<WallIndex, "planarGraph">>;

export function buildCompositeRoomSetWallIndexContext({
  building,
  memberLevelIds,
  levelRoomsByLevel,
  compositeLevelId,
}: {
  building: Pick<Building, "walls" | "wallMasses" | "dimensionBehavior">;
  memberLevelIds: string[];
  levelRoomsByLevel: Table<Room[]>;
  compositeLevelId: string;
}): CompositeRoomSetWallIndexContext | undefined {
  // validMemberLevelIds is guaranteed to overlap with memberLevelIds from collectCompositeInteriorDimensionRoomGroups
  // when levelRoomsByLevel is generated from that function.
  const validMemberLevelIds = memberLevelIds.filter((memberLevelId) => {
    return (
      levelRoomsByLevel[memberLevelId] !== undefined &&
      building.walls[memberLevelId] !== undefined &&
      building.wallMasses[memberLevelId] !== undefined &&
      building.dimensionBehavior[memberLevelId] !== undefined
    );
  });

  if (validMemberLevelIds.length === 0) {
    return undefined;
  }

  const levelHalfEdgeBase: RWTable<number> = {};
  const syntheticHalfEdgeToMemberHalfEdge: RWTable<{ levelId: string; halfEdge: number }> = {};
  let nextHalfEdgeBase = 0;
  const validMemberLevelIdSet = new Set(validMemberLevelIds);

  // Generate a synthetic half edge mapping, starting each level's half edge from the previous level's half edge count.
  // building.walls[levelId].halfEdgeToWall and building.wallMasses[levelId].halfEdgeToWall are the same array:
  // makeWallSystem() destructures halfEdgeToWall directly from the WallMassSystem and reuses it unchanged.
  // Using building.walls here for sizing and building.wallMasses elsewhere for resolution is therefore safe.
  for (const memberLevelId of validMemberLevelIds) {
    const halfEdgeCount = building.walls[memberLevelId].halfEdgeToWall.length;
    levelHalfEdgeBase[memberLevelId] = nextHalfEdgeBase;

    for (let halfEdge = 0; halfEdge < halfEdgeCount; halfEdge++) {
      syntheticHalfEdgeToMemberHalfEdge[nextHalfEdgeBase + halfEdge] = {
        levelId: memberLevelId,
        halfEdge,
      };
    }

    nextHalfEdgeBase += halfEdgeCount + 1;
  }

  const toSyntheticHalfEdge = (memberLevelId: string, halfEdge: number): number => {
    const halfEdgeBase = levelHalfEdgeBase[memberLevelId];
    assertIsNotNullOrUndefined(halfEdgeBase);
    return halfEdgeBase + halfEdge;
  };

  const syntheticDimensionBehavior: RWHalfEdgeAssociationTable<EdgeDimensionBehavior> = {};
  const syntheticWalls: RWTable<Wall> = {};
  const syntheticHalfEdgeToWall: string[] = [];

  // Generate mappings from syntheticHalfEdge to syntheticWallId, syntheticWallId to level wall, and syntheticHalfEdge to halfEdgeDimensionBehavior
  for (const memberLevelId of validMemberLevelIds) {
    const levelWalls = building.walls[memberLevelId];
    const wallIdToSyntheticWallId: RWTable<string> = {};

    for (const wallId in levelWalls.walls) {
      const syntheticWallId = `${memberLevelId}:${wallId}`;
      wallIdToSyntheticWallId[wallId] = syntheticWallId;
      syntheticWalls[syntheticWallId] = levelWalls.walls[wallId];
    }

    for (let halfEdge = 0; halfEdge < levelWalls.halfEdgeToWall.length; halfEdge++) {
      const wallId = levelWalls.halfEdgeToWall[halfEdge];
      if (!wallId) {
        continue;
      }

      const syntheticWallId = wallIdToSyntheticWallId[wallId];
      if (!syntheticWallId) {
        continue;
      }

      const syntheticHalfEdge = toSyntheticHalfEdge(memberLevelId, halfEdge);
      syntheticHalfEdgeToWall[syntheticHalfEdge] = wallIdToSyntheticWallId[wallId];
    }

    const levelDimensionBehavior = building.dimensionBehavior[memberLevelId] ?? {};
    for (const halfEdgeKey in levelDimensionBehavior) {
      const halfEdge = Number(halfEdgeKey);
      if (!Number.isFinite(halfEdge)) {
        continue;
      }

      const syntheticHalfEdge = toSyntheticHalfEdge(memberLevelId, halfEdge);
      syntheticDimensionBehavior[syntheticHalfEdge] = levelDimensionBehavior[halfEdge];
    }
  }

  const syntheticWallIndex: InteriorDimensionWallIndex = {
    // Get composite roomSet, return concatenated wall strips from member room sets.
    solidWallStripsInRoomSet(inputRoomSet, rooms, _levelRooms, linearize = false, includeWallOffset = false) {
      const wallStrips: ReturnType<InteriorDimensionWallIndex["solidWallStripsInRoomSet"]> = [];

      const inputRoomSetByLevel = splitRoomSetByLevel(inputRoomSet);

      for (const memberLevelId in inputRoomSetByLevel) {
        if (!validMemberLevelIdSet.has(memberLevelId)) {
          continue;
        }

        const memberRoomSet = inputRoomSetByLevel[memberLevelId];
        const memberWallMassSystem = building.wallMasses[memberLevelId];
        const memberLevelRooms = levelRoomsByLevel[memberLevelId];

        if (memberRoomSet.length === 0 || !memberWallMassSystem || !memberLevelRooms) {
          continue;
        }

        wallStrips.push(
          ...memberWallMassSystem.wallIndex.solidWallStripsInRoomSet(
            memberRoomSet,
            rooms,
            memberLevelRooms,
            linearize,
            includeWallOffset,
          ),
        );
      }

      return wallStrips;
    },
    // Gets collinear strips from each member level and returns the concatenated result
    mergeCollinearWallStripsReturnHalfEdges(wallStrips) {
      const wallStripsByLevel = new Map<string, typeof wallStrips>();

      for (const wallStrip of wallStrips) {
        const memberLevelId = wallStrip.room.level.id;
        const memberLevelWallStrips = wallStripsByLevel.get(memberLevelId);

        if (memberLevelWallStrips) {
          memberLevelWallStrips.push(wallStrip);
        } else {
          wallStripsByLevel.set(memberLevelId, [wallStrip]);
        }
      }

      const segments: ReturnType<InteriorDimensionWallIndex["mergeCollinearWallStripsReturnHalfEdges"]>["segments"] =
        [];
      const segmentHalfEdges: ReturnType<
        InteriorDimensionWallIndex["mergeCollinearWallStripsReturnHalfEdges"]
      >["segmentHalfEdges"] = [];

      for (const [memberLevelId, memberLevelWallStrips] of wallStripsByLevel) {
        const memberWallMassSystem = building.wallMasses[memberLevelId];
        if (!memberWallMassSystem || memberLevelWallStrips.length === 0) {
          continue;
        }

        const { segments: memberLevelSegments, segmentHalfEdges: memberLevelSegmentHalfEdges } =
          memberWallMassSystem.wallIndex.mergeCollinearWallStripsReturnHalfEdges(memberLevelWallStrips);

        segments.push(...memberLevelSegments);
        segmentHalfEdges.push(
          ...memberLevelSegmentHalfEdges.map((memberLevelHalfEdges) =>
            memberLevelHalfEdges.map((halfEdge) => toSyntheticHalfEdge(memberLevelId, halfEdge)),
          ),
        );
      }

      return { segments, segmentHalfEdges };
    },
  };

  const wallLevelId = `${COMPOSITE_ROOM_SET_WALL_LEVEL_ID_PREFIX}${compositeLevelId}`;
  const syntheticWallSystem = {
    walls: syntheticWalls,
    halfEdgeToWall: syntheticHalfEdgeToWall,
  };

  return {
    wallIndex: syntheticWallIndex,
    wallLevelId,
    wallTable: { [wallLevelId]: syntheticWallSystem },
    dimensionBehavior: syntheticDimensionBehavior,
    // Maps from synthetic edge indices back to member level halfEdgeData
    resolveHalfEdgeData: (syntheticHalfEdges) => {
      const halfEdgeData: EdgeData[] = [];
      for (const syntheticHalfEdge of syntheticHalfEdges) {
        const memberHalfEdgeData = syntheticHalfEdgeToMemberHalfEdge[syntheticHalfEdge];
        if (!memberHalfEdgeData) {
          continue;
        }
        halfEdgeData.push(halfEdgeDataNew(memberHalfEdgeData.levelId, memberHalfEdgeData.halfEdge));
      }
      return halfEdgeData;
    },
    // Maps from synthetic edge index back to member level wall path data
    resolveHalfEdgeLine: (syntheticHalfEdge) => {
      const memberHalfEdgeData = syntheticHalfEdgeToMemberHalfEdge[syntheticHalfEdge];
      if (!memberHalfEdgeData) {
        return undefined;
      }

      const memberWallMassSystem = building.wallMasses[memberHalfEdgeData.levelId];
      if (!memberWallMassSystem) {
        return undefined;
      }

      const memberWallId = memberWallMassSystem.halfEdgeToWall[memberHalfEdgeData.halfEdge];
      if (!memberWallId) {
        return undefined;
      }

      const memberWall = memberWallMassSystem.walls[memberWallId];
      if (!memberWall) {
        return undefined;
      }

      const wallPath = getWallPath(memberWall);
      const isForwardHalfEdge = memberHalfEdgeData.halfEdge === wallPath.halfEdgeIndex;
      if (isForwardHalfEdge) {
        return [wallPath.startX, wallPath.startY, wallPath.endX, wallPath.endY];
      }

      return [wallPath.endX, wallPath.endY, wallPath.startX, wallPath.startY];
    },
  };
}

function splitRoomSetByLevel(roomSet: RoomSet): Table<RoomSet> {
  const roomSetsByLevel: RWTable<RoomSet> = {};

  for (const room of roomSet) {
    const roomLevelId = room.level.id;
    if (!roomSetsByLevel[roomLevelId]) {
      roomSetsByLevel[roomLevelId] = [];
    }
    roomSetsByLevel[roomLevelId].push(room);
  }

  return roomSetsByLevel;
}
